import type { OAuthProviderName } from '@nest-react/domain';

export interface RegionalApiClient {
  readonly apiUrl: string;
  request: <T>(path: string, options?: RequestInit) => Promise<T>;
  oauthUrl: (
    provider: OAuthProviderName,
    returnTo: string,
    link?: boolean
  ) => string;
  deactivate: () => void;
}

export class ApiRequestError extends Error {
  constructor(
    message: string,
    public readonly status: number
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

let activeClient: RegionalApiClient | null = null;

export const setActiveRegionalApiClient = (
  client: RegionalApiClient | null
): void => {
  activeClient = client;
};

export const requestWithActiveRegionalClient = <T>(
  path: string,
  options?: RequestInit
): Promise<T> => {
  if (!activeClient)
    return Promise.reject(new Error('Regional API is not ready'));
  return activeClient.request<T>(path, options);
};

export const createRegionalApiClient = (
  rawApiUrl: string
): RegionalApiClient => {
  const apiUrl = rawApiUrl.replace(/\/$/, '');
  const controller = new AbortController();
  let active = true;

  return {
    apiUrl,
    async request<T>(path: string, options: RequestInit = {}): Promise<T> {
      if (!active) throw new Error('Region API client is inactive');
      const response = await fetch(`${apiUrl}${path}`, {
        ...options,
        credentials: 'include',
        signal: controller.signal,
        headers: {
          ...(options.body ? { 'content-type': 'application/json' } : {}),
          ...options.headers,
        },
      });
      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as {
          message?: string | string[];
        };
        const message = Array.isArray(payload.message)
          ? payload.message.join(', ')
          : payload.message;
        throw new ApiRequestError(
          message || `Request failed (${response.status})`,
          response.status
        );
      }
      return response.json() as Promise<T>;
    },
    oauthUrl(provider, returnTo, link = false) {
      const query = new URLSearchParams({ returnTo });
      if (link) query.set('link', 'true');
      return `${apiUrl}/auth/${provider}/start?${query.toString()}`;
    },
    deactivate() {
      active = false;
      controller.abort();
      if (activeClient === this) activeClient = null;
    },
  };
};
